import { Link } from 'react-router-dom';
import TrustWealthLogo from '../TrustWealthLogo';
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon,
} from '@heroicons/react/24/outline';

// Social Media Icons
const FacebookIcon = () => (
  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
  </svg>
);

const TwitterIcon = () => (
  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" />
  </svg>
);

const InstagramIcon = () => (
  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const NCUALogo = () => (
  <svg width="40" height="40" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M50 0L93.3 25V75L50 100L6.7 75V25L50 0Z" fill="#123524"/>
    <path d="M50 10L83.3 30V70L50 90L16.7 70V30L50 10Z" fill="white"/>
    <text x="50" y="52" textAnchor="middle" fill="#123524" fontSize="18" fontWeight="bold">NCUA</text>
    <text x="50" y="68" textAnchor="middle" fill="#123524" fontSize="10">INSURED</text>
  </svg>
);

const Footer = () => {
  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Locations', href: '/locations' },
    { name: 'Careers', href: '/careers' },
    { name: 'Security', href: '/security' },
  ];

  const products = [
    { name: 'Current Account', href: '/accounts/current' },
    { name: 'Savings', href: '/accounts/savings' },
    { name: 'Credit Cards', href: '/credit-cards' },
    { name: 'Mortgages', href: '/mortgages' },
  ];

  const resources = [
    { name: 'Help Center', href: '/help' },
    { name: 'Financial Education', href: '/education' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact Us', href: '/contact' },
  ];

  return (
    <footer className="bg-white/95 relative">
      <div className="absolute inset-0 bg-pattern-dots opacity-5"></div>
      
      {/* Main Footer Content */}
      <div className="relative container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <TrustWealthLogo />
            <p className="text-gray-600 text-sm">
              Providing trusted financial services and personalized solutions since 1973.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-gray-400 hover:text-primary transition-colors">
                <FacebookIcon />
              </a>
              <a href="#" className="text-gray-400 hover:text-primary transition-colors">
                <TwitterIcon />
              </a>
              <a href="#" className="text-gray-400 hover:text-primary transition-colors">
                <LinkedInIcon />
              </a>
              <a href="#" className="text-gray-400 hover:text-primary transition-colors">
                <InstagramIcon />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-gray-600 hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4">Products</h3>
            <ul className="space-y-3">
              {products.map((product) => (
                <li key={product.name}>
                  <Link 
                    to={product.href}
                    className="text-gray-600 hover:text-primary transition-colors"
                  >
                    {product.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li>
                <a 
                  href="mailto:support@willowvalecu.com"
                  className="flex items-center text-gray-600 hover:text-primary transition-colors"
                >
                  <EnvelopeIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                  support@willowvalecu.com
                </a>
              </li>
              <li>
                <a 
                  href="tel:+18088602349"
                  className="flex items-center text-gray-600 hover:text-primary transition-colors"
                >
                  <PhoneIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                  (808) 860-2349
                </a>
              </li>
              <li>
                <a 
                  href="https://www.google.com/maps/search/600+Kapiolani+Blvd,+Honolulu,+HI+96813"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start text-gray-600 hover:text-primary transition-colors"
                >
                  <MapPinIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span>600 Kapiolani Blvd, Honolulu, HI 96813, USA</span>
                </a>
              </li>
              <li className="text-gray-600 text-sm">
                Reg No: 0827788
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative border-t border-gray-200">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <NCUALogo />
              <div className="text-sm text-gray-600">
                <div>NCUA Insured</div>
                <div>Federally insured by NCUA</div>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-gray-600">
              <Link to="/privacy" className="hover:text-primary transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-primary transition-colors">
                Terms of Service
              </Link>
              <Link to="/accessibility" className="hover:text-primary transition-colors">
                Accessibility
              </Link>
              <div>
                <span>Routing #: </span>
                <span className="font-mono">021000021</span>
              </div>
              <div>
                <span>Reg No: </span>
                <span className="font-mono">0827788</span>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              © {new Date().getFullYear()} Willowvale Credit Union
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 